namespace Solovis.Portal.TxServices {

    export interface GroupedProFormaTxViewEditModelOptions {
        groupedProFormaTxListViewEditModel?: GroupedProFormaTxListViewEditModel;
        tx?: ProFormaTransactionEntryEditModel;
    }

    /**
     * A real transaction found via the Link Txn search (SE-287), held on the row as
     * in-dialog UI state only. Not part of ProFormaTransactionEntryEditModel / the save
     * contract - persisting the link is PF2-023's job.
     */
    export interface LinkedTransactionCandidate {
        transactionId: number;
        resourceId?: number;
        label: string;
    }

    export class GroupedProFormaTxViewEditModel {
        private _groupedProFormaTxListViewEditModel: GroupedProFormaTxListViewEditModel | null;

        private _investmentId: number | null;
        private _ownerId: number | null;
        private _holdingId: number | null;
        private _transactionEntryId: number | null;
        private _transactionEntryResourceId: number | null;
        public editId: string | null;
        private _effectiveDayId: number | null;
        private _effectiveTimeUtc: Date | number | null;
        private _transactionTypeTagId: number | null;
        private _transactionTypeTagSetId: number | null;
        private _netAmountIn: number | string;
        private _isStartOfDay: boolean;
        private _netPercentAmountIn: number | string;
        private _txComment: string | null;
        private _isAdjustment: boolean | null;
        private _reviewedBy: number | null;
        private _linkedTransactionCandidate: LinkedTransactionCandidate | null = null;

        /**
         * Pro Forma Phase II grouping identity (Grouping Property / tag value a scaffold-row trade
         * was entered against). Not user-editable in this dialog -- carried through unchanged from
         * load to save so it isn't clobbered with null on an unrelated edit.
         */
        private _propertyAssociationId: number | null;
        private _tagAssociationId: number | null;

        private _editLabel;

        private _propDefIdToCustomValueMap: Record<number, any>;
        private _originalPropDefIdToCustomValueMap: Record<number, any>;


        // TODO: Declare set of system tag set ids in InvData.
        public static defaultTxTypeTagSetId = 102;


        constructor(options: GroupedProFormaTxViewEditModelOptions) {
            const tx: ProFormaTransactionEntryEditModel = options.tx ?? {};

            this._groupedProFormaTxListViewEditModel = options.groupedProFormaTxListViewEditModel ?? null;

            const portalServices = this._groupedProFormaTxListViewEditModel?.portalServices();

            this._investmentId = tx.investmentId ?? null;
            this._ownerId = tx.ownerId ?? null;
            this._holdingId = tx.holdingId ?? null;
            this._transactionEntryId = tx.transactionEntryId ? tx.transactionEntryId : null;
            this._transactionEntryResourceId = tx.transactionEntryResourceId ? tx.transactionEntryResourceId : null;
            this.editId = tx.transactionEntryId ? null :
                (tx.editId ? tx.editId : _.uniqueId("_GroupedProFormaTxViewEditModel_"));

            this._effectiveDayId = tx.effectiveDayId ? tx.effectiveDayId : null;
            this._effectiveTimeUtc = tx.effectiveTimeUtc ? tx.effectiveTimeUtc : null;
            this._transactionTypeTagId = tx.transactionTypeTagId ? tx.transactionTypeTagId : null;
            this._transactionTypeTagSetId = tx.transactionTypeTagSetId ? tx.transactionTypeTagSetId : GroupedProFormaTxViewEditModel.defaultTxTypeTagSetId;
            this._netAmountIn = tx.netAmountIn ? tx.netAmountIn.toString() : "0";
            this._netPercentAmountIn = tx.netPercentAmountIn ? tx.netPercentAmountIn.toString() : "0";
            this._isStartOfDay = Solovis.isNotNull(tx.isStartOfDay) ? !!tx.isStartOfDay : TxSettings.defaultTxIsStartOfDay(portalServices);
            this._txComment = tx.txComment ? tx.txComment.toString() : null;
            this._isAdjustment = tx.isAdjustment ? true : false;

            this._reviewedBy = tx.reviewedBy ?? null;

            this._propertyAssociationId = tx.propertyAssociationId ?? null;
            this._tagAssociationId = tx.tagAssociationId ?? null;

            this._propDefIdToCustomValueMap = {};
            this._originalPropDefIdToCustomValueMap = {};
            if (tx.customPropValues) {
                for (const customPropValue of tx.customPropValues) {
                    this._propDefIdToCustomValueMap[customPropValue.propDefId] = customPropValue.value;
                    this._originalPropDefIdToCustomValueMap[customPropValue.propDefId] = customPropValue.value;
                }
            }
        }


        public getProFormaTxListViewEditModel(): GroupedProFormaTxListViewEditModel {
            return this._groupedProFormaTxListViewEditModel;
        }

        public getReviewedBy = () => {
          return this._reviewedBy;
        }



        public investmentId(investmentId?: typeof this._investmentId) {
            if (!arguments.length) {
                return this._investmentId;
            }
            else {
                if (investmentId) {
                    const newInvestmentId = typeof investmentId === "string" ? parseInt(investmentId) : investmentId;
                    this._investmentId = newInvestmentId;
                }
                else {
                    this._investmentId = null;
                }
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public ownerId(ownerId?: typeof this._ownerId) {
            if (!arguments.length) {
                return this._ownerId;
            }
            else {
                if (ownerId) {
                    const newOwnerId = typeof ownerId === "string" ? parseInt(ownerId) : ownerId;
                    this._ownerId = newOwnerId;
                }
                else {
                    this._ownerId = null;
                }
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public holdingId(holdingId?: typeof this._holdingId) {
            if (!arguments.length) {
                return this._holdingId;
            }
            else {
                if (holdingId) {
                    const newHoldingId = typeof holdingId === "string" ? parseInt(holdingId) : holdingId;
                    this._holdingId = newHoldingId;
                }
                else {
                    this._holdingId = null;
                }
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public transactionEntryId(transactionEntryId?: typeof this._transactionEntryId) {
            if (!arguments.length) {
                return this._transactionEntryId;
            }
            else {
                if (isNaN(Number(transactionEntryId))) {
                    throw "invalid transaction Id.";
                }
                this._transactionEntryId = transactionEntryId ?? null;
                this.editId = null;
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public effectiveDayId(effectiveDayId?: typeof this._effectiveDayId) {
            if (!arguments.length) {
                return this._effectiveDayId;
            }
            else {
                if (isNaN(Number(effectiveDayId))) {
                    throw "Invalid date format.";
                }
                this._effectiveDayId = effectiveDayId ?? null;
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public effectiveTimeUtc(effectiveTimeUtc?: string | number) {
            if (!arguments.length) {
                return this._effectiveTimeUtc;
            }
            else {
                const newTimeUtc = new Date(effectiveTimeUtc as string | number);
                if (isNaN(newTimeUtc.valueOf())) {
                    throw "Invalid date format.";
                }
                this._effectiveTimeUtc = newTimeUtc;
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public transactionTypeTagId(transactionTypeTagId?: typeof this._transactionTypeTagId) {
            if (!arguments.length) {
                return this._transactionTypeTagId;
            }
            else {
                let newTagId = null;
                if (Solovis.isNotNull(transactionTypeTagId)) {
                    newTagId = transactionTypeTagId;
                }
                this._transactionTypeTagId = newTagId;
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public transactionTypeTagSetId(transactionTypeTagSetId?: typeof this._transactionTypeTagSetId) {
            if (!arguments.length) {
                return this._transactionTypeTagSetId;
            }
            else {
                if (isNaN(Number(transactionTypeTagSetId))) {
                    throw "invalid tag set Id.";
                }
                const newTagSetId = transactionTypeTagSetId;
                this._transactionTypeTagSetId = newTagSetId ?? null;
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public netAmountIn(netAmountIn?: typeof this._netAmountIn) {
            if (!arguments.length) {
                return this._netAmountIn;
            }
            else {
                if (Solovis.isNull(netAmountIn)) {
                    netAmountIn = "0";
                }

                this._netAmountIn = netAmountIn.toString();
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public netPercentAmountIn(netPercentAmountIn?: typeof this._netPercentAmountIn) {
            if (!arguments.length) {
                return this._netPercentAmountIn;
            }
            else {
                if (Solovis.isNull(netPercentAmountIn)) {
                    netPercentAmountIn = "0";
                }

                this._netPercentAmountIn = netPercentAmountIn.toString();
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public isStartOfDay(isStartOfDay?: typeof this._isStartOfDay) {
            if (!arguments.length) {
                return this._isStartOfDay;
            }
            else {
                this._isStartOfDay = isStartOfDay ? true : false;
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public txComment(txComment?: typeof this._txComment) {
            if (!arguments.length) {
                return this._txComment;
            }
            else {
                this._txComment = txComment ?? null;
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public isAdjustment(isAdjustment?: typeof this._isAdjustment) {
            if (!arguments.length) {
                return this._isAdjustment;
            }
            else {
                this._isAdjustment = isAdjustment ? true : false;
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public linkedTransactionCandidate(candidate?: LinkedTransactionCandidate | null) {
            if (!arguments.length) {
                return this._linkedTransactionCandidate;
            }
            else {
                this._linkedTransactionCandidate = candidate ?? null;
                this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                    operation: "transactionEdit",
                    transaction: this
                });
            }
            return null;
        }


        public getId() {
            const result: {editId: boolean, value: string | number | null} = {
                editId: false,
                value: null
            };

            if (Solovis.isNotNull(this.editId)) {
                result.editId = true;
                result.value = this.editId;
            }
            else {
                const txId = this.transactionEntryId();
                if (Solovis.isNotNull(txId)) {
                    result.value = txId;
                }
            }
            if (result.value === null) {
                throw "GroupedProFormaTxViewEditModel has no editId and no transactionEntryId.";
            }
            return result;
        }


        public isPrimary() {
            const primaryTransactionEntryId = this._groupedProFormaTxListViewEditModel?.primaryTransactionEntryId();
            const primaryTransactionEditId = this._groupedProFormaTxListViewEditModel?.primaryTransactionEditId();
            if (Solovis.isNotNull(primaryTransactionEntryId)) {
                if (this.transactionEntryId() === primaryTransactionEntryId) {
                    return true;
                }
            }
            else if (Solovis.isNotNull(primaryTransactionEditId)) {
                if (this.editId === primaryTransactionEditId) {
                    return true;
                }
            }
            return false;
        }

        public equalById(otherProFormaTxViewEditModel: GroupedProFormaTxViewEditModel) {
            const myTxEntryId = this.transactionEntryId();
            const otherTxEntryId = otherProFormaTxViewEditModel.transactionEntryId();
            if (Solovis.isNotNull(myTxEntryId) && Solovis.isNotNull(otherTxEntryId)) {
                if (myTxEntryId === otherTxEntryId) {
                    return true;
                }
            }

            if (Solovis.isNotNull(this.editId) && Solovis.isNotNull(otherProFormaTxViewEditModel.editId)) {
                if (this.editId === otherProFormaTxViewEditModel.editId) {
                    return true;
                }
            }

            return false;
        }

        public togglePrimary(toggleValue: boolean) {
            if (toggleValue === true) {
                const txEntryId = this.transactionEntryId();

                if (Solovis.isNotNull(txEntryId)) {
                    this._groupedProFormaTxListViewEditModel?.primaryTransactionEntryId(txEntryId);
                }

                else {
                    this._groupedProFormaTxListViewEditModel?.primaryTransactionEditId(this.editId);
                }
            }
            else if (toggleValue === false) {
                this._groupedProFormaTxListViewEditModel?.primaryTransactionEntryId(null);
                this._groupedProFormaTxListViewEditModel?.primaryTransactionEditId(null);
            }
        }

        public getTransactionNumericData() {
            throw "Not implemented.";
        }

        public validate() {
            const errorMessages: string[] = [];

            if (Solovis.isNull(this.effectiveDayId())) {
                errorMessages.push("Effective Date is required.");
            }

            const ownerId = this.ownerId();
            const holdingId = this.holdingId();
            if (Solovis.isNotNull(ownerId) && ownerId === holdingId) {
                errorMessages.push("Owner and Holding cannot be the same entity.");
            }

            return {
                isValid: errorMessages.length === 0,
                errorMessages: errorMessages
            };
        }

        public editLabel() {
            const self = this;

            if (Solovis.isNotNull(this._editLabel)) {
                return this._editLabel;
            }
            const txEntryId = this.transactionEntryId();
            if (Solovis.isNotNull(txEntryId)) {
                this._editLabel = txEntryId;
                return this._editLabel;
            }

            // If we do not have a transaction entry Id, return a unique (to the list) letter.
            let index = this._groupedProFormaTxListViewEditModel?.indexOf(this) ?? -1;
            if (index > -1) {
                if (index < 26) {
                    this._editLabel = String.fromCharCode(65 + index);
                }
                else {
                    index = index - 26;
                    this._editLabel = String.fromCharCode(97 + index);
                }
            }
            // Make sure this editLabel doesn't already exist
            $(this._groupedProFormaTxListViewEditModel?.groupedProFormaTxViewEditModels() ?? []).each(function () {
                if (!this.equalById(self)) {
                    if (this.editLabel() === self._editLabel) {
                        let charCode = self._editLabel.charCodeAt(0);
                        charCode++;
                        if (charCode < 91) {
                            self._editLabel = String.fromCharCode(charCode);
                        }
                        else {
                            charCode = charCode - 91;
                            self._editLabel = String.fromCharCode(97 + charCode);
                        }
                    }
                }
            });

            return this._editLabel;
        }

        public getCustomPropValue(propDefId: number) {
            return this._propDefIdToCustomValueMap[propDefId];
        }

        public getOriginalCustomPropValue(propDefId: number){
            return this._originalPropDefIdToCustomValueMap[propDefId];
        }

        public getAllCustomPropValues() {
            return this._propDefIdToCustomValueMap;
        }

        public setCustomPropValue(propDefId: number, value: any) {
            this._propDefIdToCustomValueMap[propDefId] = value;

            this._groupedProFormaTxListViewEditModel?.notifyModelChanged({
                operation: "transactionEdit",
                transaction: this
            });
        }


        /*
        public calculateNetAmountInAsNumber(): number | null {
            let amountIn = this.amountIn();
            let amountOut = this.amountOut();

            if (typeof (amountIn) === "string") {
                amountIn = InvData.DecimalHelper.decimalStringAsNumber(amountIn, true);
            }
            if (typeof (amountOut) === "string") {
                amountOut = InvData.DecimalHelper.decimalStringAsNumber(amountOut, true);
            }

            if (Solovis.isNotNull(amountIn) && Solovis.isNotNull(amountOut)) {
                let netAmountIn = amountIn - amountOut;
                return netAmountIn;
            }
            else {
                return null;
            }
        }

        public calculateNetAmountInAsString(): number | string | null {
            let netAmountIn = this.calculateNetAmountInAsNumber();
            if (isNull(netAmountIn)) {
                return null;
            }

            let amountIn = this.amountIn();
            let amountOut = this.amountOut();

            // If amountIn or amountOut is null or 0, we can preserve original input string.
            if (InvData.DecimalHelper.isNullOrZero(amountOut)) {
                return amountIn;
            }
            else if (InvData.DecimalHelper.isNullOrZero(amountIn)) {
                let negAmountOut = InvData.DecimalHelper.negate(amountOut);
                return negAmountOut;
            }

            let scaleToUse = InvData.DecimalHelper.getScale(amountIn);
            scaleToUse = Math.max(scaleToUse, InvData.DecimalHelper.getScale(amountOut));

            let netAmountInStr = InvData.DecimalHelper.clampNumberToScaleAsString(netAmountIn, scaleToUse);
            netAmountInStr = InvData.DecimalHelper.fixupThousandsSeparatorAsNumberString(netAmountInStr);

            return netAmountInStr;
        }
            */


        public getTransactionData(): ProFormaTransactionEntryEditModel {
            const txData: ProFormaTransactionEntryEditModel = {
                investmentId: this.investmentId() ?? undefined,
                transactionEntryId: this.transactionEntryId() ?? undefined,
                transactionEntryResourceId: this._transactionEntryResourceId ?? undefined,
                editId: this.editId ?? undefined,
                effectiveDayId: this.effectiveDayId() ?? undefined,
                effectiveTimeUtc: this.effectiveTimeUtc() as number ?? undefined,
                transactionTypeTagId: this.transactionTypeTagId() ?? undefined,
                txComment: this.txComment() ?? undefined,
                isAdjustment: this.isAdjustment() ?? undefined,
                propertyAssociationId: this._propertyAssociationId ?? undefined,
                tagAssociationId: this._tagAssociationId ?? undefined,
            };

            const propDefIdsToEdit = this.getProFormaTxListViewEditModel().getPropDefIdsToEdit();
            if (!_.isEmpty(propDefIdsToEdit)) {
                const customPropValues: TxCustomPropValue[] = [];
                for (const propDefId of propDefIdsToEdit) {
                    const value = this.getCustomPropValue(propDefId);
                    customPropValues.push({
                        propDefId: propDefId,
                        value: value
                    });
                }

                txData.customPropValues = customPropValues;
            }

            return txData;
        }

        public getTransactionDataToSave(): ProFormaTransactionEntryEditModel {
            const txData: ProFormaTransactionEntryEditModel = {
                investmentId: this.investmentId() ?? undefined,
                ownerId: this.ownerId() ?? undefined,
                holdingId: this.holdingId() ?? undefined,
                transactionEntryId: this.transactionEntryId() ?? undefined,
                transactionEntryResourceId: this._transactionEntryResourceId ?? undefined,
                editId: this.editId ?? undefined,
                effectiveDayId: this.effectiveDayId() ?? undefined,
                effectiveTimeUtc: this.effectiveTimeUtc() as number,
                transactionTypeTagId: this.transactionTypeTagId() ?? undefined,
                transactionTypeTagSetId: this.transactionTypeTagSetId() ?? undefined,
                netAmountIn: this.netAmountIn() ?? undefined,
                netPercentAmountIn: this.netPercentAmountIn() ?? undefined,
                isStartOfDay: this.isStartOfDay(),
                txComment: this.txComment() ?? undefined,
                isAdjustment: this.isAdjustment() ?? undefined,
                propertyAssociationId: this._propertyAssociationId ?? undefined,
                tagAssociationId: this._tagAssociationId ?? undefined,
            };

            const propDefIdsToEdit = this.getProFormaTxListViewEditModel().getPropDefIdsToEdit();
            if (!_.isEmpty(propDefIdsToEdit)) {
                const customPropValues: TxCustomPropValue[] = [];
                for (const propDefId of propDefIdsToEdit) {
                    const value = this.getCustomPropValue(propDefId);
                    // check if value has changed
                    const orgValue = this.getOriginalCustomPropValue(propDefId);
                    if(value !== orgValue){
                        customPropValues.push({
                            propDefId: propDefId,
                            value: value
                        });
                    }                    
                }

                txData.customPropValues = customPropValues;
            }

            return txData;
        }
    }

}